// Les cinq types d'étude, et ce que chacun demande de préparer.
//
// Le TYPE décide de l'assistant : un entretien se prépare avec un guide
// (thèmes, questions, relances), une observation avec une grille, un
// questionnaire avec des questions typées. Un test utilisateur et un audit ont
// déjà leur outil complet : l'étude les crée et pointe vers eux.
import type { AnswerType, Study, StudyKind, StudyProtocol } from '../api/types';

export interface KindSpec {
  kind: StudyKind;
  /** Libellé canonique, enregistré dans `studies.type`. */
  label: string;
  /** Ce que l'étude permet — affiché sur la carte de choix. */
  purpose: string;
  /** Ce que l'assistant fera préparer. */
  prepares: string;
  protocolName: string;
  /** Le nom avec son article, pour les phrases (« la grille d’observation »). */
  protocolThe: string;
  methods: string[];
  participantsLabel: string;
  participantsHint: string;
  /** Protocole porté par l'étude (sinon par le test ou l'audit lié). */
  hasProtocol: boolean;
  sectionLabel: string;
  itemLabel: string;
  itemPlural: string;
  itemPlaceholder: string;
  introLabel: string;
  durationLabel: string;
  suggestLabel: string;
}

export const KINDS: KindSpec[] = [
  {
    kind: 'entretien',
    label: 'Entretiens',
    purpose: 'Comprendre les usages, les motivations et les irritants en faisant raconter des situations vécues.',
    prepares: 'Guide d’entretien : thèmes, questions ouvertes, relances.',
    protocolName: 'Guide d’entretien',
    protocolThe: 'le guide d’entretien',
    methods: ['Entretiens semi-directifs', 'Entretiens directifs', 'Entretiens exploratoires', 'Focus group'],
    participantsLabel: 'Participants',
    participantsHint: '5 à 8 par profil suffisent souvent à voir les motifs se répéter.',
    hasProtocol: true,
    sectionLabel: 'Thème',
    itemLabel: 'Question',
    itemPlural: 'questions',
    itemPlaceholder: 'Racontez-moi la dernière fois que vous avez…',
    introLabel: 'Introduction — présentation, consentement, enregistrement',
    durationLabel: 'Durée d’un entretien (min)',
    suggestLabel: 'guide',
  },
  {
    kind: 'observation',
    label: 'Observation terrain',
    purpose: 'Voir le travail réel dans son contexte : gestes, outils, contournements, interruptions.',
    prepares: 'Grille d’observation : lieux, moments, points à observer.',
    protocolName: 'Grille d’observation',
    protocolThe: 'la grille d’observation',
    methods: ['Shadowing', 'Enquête contextuelle', 'Observation passive', 'Observation participante'],
    participantsLabel: 'Sessions',
    participantsHint: 'Nombre de sessions d’observation prévues.',
    hasProtocol: true,
    sectionLabel: 'Moment',
    itemLabel: 'Point à observer',
    itemPlural: 'points à observer',
    itemPlaceholder: 'Les outils utilisés en parallèle (papier, téléphone) et pourquoi',
    introLabel: 'Consignes — posture, accord, prise de notes',
    durationLabel: 'Durée d’une session (min)',
    suggestLabel: 'grille',
  },
  {
    kind: 'test',
    label: 'Test utilisateur',
    purpose: 'Mesurer si les utilisateurs réussissent des tâches clés, et repérer où ils bloquent.',
    prepares: 'Scénario : les tâches à faire réaliser. Les issues se notent sur la fiche du test.',
    protocolName: 'Scénario de test',
    protocolThe: 'le scénario de test',
    methods: ['Test modéré en présentiel', 'Test modéré à distance', 'Test non modéré'],
    participantsLabel: 'Participants',
    participantsHint: '5 participants font apparaître l’essentiel des problèmes d’utilisabilité.',
    hasProtocol: false,
    sectionLabel: '',
    itemLabel: 'Tâche',
    itemPlural: 'tâches',
    itemPlaceholder: 'Enregistrer une sortie de stock pour un chantier',
    introLabel: '',
    durationLabel: '',
    suggestLabel: '',
  },
  {
    kind: 'questionnaire',
    label: 'Questionnaire',
    purpose: 'Mesurer à plus grande échelle : profils, fréquences d’usage, satisfaction.',
    prepares: 'Questions typées : ouvertes, échelles, choix.',
    protocolName: 'Questionnaire',
    protocolThe: 'le questionnaire',
    methods: ['Questionnaire en ligne', 'Questionnaire dans l’application', 'Questionnaire papier'],
    participantsLabel: 'Répondants visés',
    participantsHint: 'Au moins 30 réponses par profil pour comparer des proportions.',
    hasProtocol: true,
    sectionLabel: 'Partie',
    itemLabel: 'Question',
    itemPlural: 'questions',
    itemPlaceholder: 'À quelle fréquence utilisez-vous…',
    introLabel: 'Texte d’introduction affiché aux répondants',
    durationLabel: 'Durée estimée (min)',
    suggestLabel: 'questionnaire',
  },
  {
    kind: 'audit',
    label: 'Audit UX',
    purpose: 'Évaluer une interface sans utilisateurs, contre un référentiel d’heuristiques.',
    prepares: 'Périmètre de l’audit : version, parcours, catégories. La notation se fait sur la fiche de l’audit.',
    protocolName: 'Grille d’audit',
    protocolThe: 'la grille d’audit',
    methods: ['Heuristiques de Nielsen', 'Critères de Bastien et Scapin', 'Accessibilité (RGAA)'],
    participantsLabel: '',
    participantsHint: '',
    hasProtocol: false,
    sectionLabel: '',
    itemLabel: '',
    itemPlural: '',
    itemPlaceholder: '',
    introLabel: '',
    durationLabel: '',
    suggestLabel: '',
  },
];

export const KIND_BY_KEY: Record<StudyKind, KindSpec> = Object.fromEntries(
  KINDS.map((k) => [k.kind, k]),
) as Record<StudyKind, KindSpec>;

export const ANSWER_TYPES: { value: AnswerType; label: string }[] = [
  { value: 'ouverte', label: 'Réponse libre' },
  { value: 'echelle', label: 'Échelle' },
  { value: 'choix_unique', label: 'Choix unique' },
  { value: 'choix_multiple', label: 'Choix multiple' },
  { value: 'oui_non', label: 'Oui / non' },
];

function fold(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Même règle que le serveur (`studies/protocol.js`) : on range par mots-clés,
 *  ce qui ne se range pas vaut `null` (« autre »), jamais un type deviné. */
export function studyKind(type: string | null | undefined): StudyKind | null {
  const t = fold(type ?? '');
  if (!t.trim()) return null;
  if (/entretien|interview|focus/.test(t)) return 'entretien';
  if (/observ|shadow|immersion|contextuel/.test(t)) return 'observation';
  if (/questionnaire|enquete|sondage|survey/.test(t)) return 'questionnaire';
  if (/audit|heuristi/.test(t)) return 'audit';
  if (/test|utilisabilit/.test(t)) return 'test';
  return null;
}

export function protocolSize(p: StudyProtocol | null | undefined): number {
  return p?.sections.reduce((n, s) => n + s.items.length, 0) ?? 0;
}

/** Résumé d'une ligne pour la carte d'une étude. */
export function protocolSummary(study: Study): string | null {
  const spec = KIND_BY_KEY[studyKind(study.type) as StudyKind];
  if (!spec) return null;
  if (spec.kind === 'test') return study.test_id ? `Test lié · ${study.test_code ?? 'scénario'}` : null;
  if (spec.kind === 'audit') return study.audit_id ? `Audit lié · ${study.audit_code ?? study.audit_title ?? ''}`.trim() : null;
  const n = protocolSize(study.protocol);
  if (n === 0) return `${spec.protocolName} à préparer`;
  return `${spec.protocolName} · ${n} ${n > 1 ? spec.itemPlural : spec.itemLabel.toLowerCase()}`;
}

export function emptyProtocol(): StudyProtocol {
  return { durationMin: null, intro: '', closing: '', sections: [] };
}

/** Le protocole en texte brut — à coller dans un document ou à imprimer. */
export function protocolAsText(spec: KindSpec, p: StudyProtocol, title: string): string {
  const lines: string[] = [`${spec.protocolName} — ${title}`];
  if (p.durationMin) lines.push(`Durée : ${p.durationMin} min`);
  if (p.places) lines.push('', 'Lieux et situations :', p.places);
  if (p.intro) lines.push('', 'Introduction :', p.intro);
  let n = 0;
  for (const s of p.sections) {
    lines.push('', s.title ? s.title.toUpperCase() : '—');
    for (const it of s.items) {
      n += 1;
      lines.push(`${n}. ${it.text}`);
      if (it.followups) lines.push(`   Relances : ${it.followups}`);
      if (it.answerType && it.answerType !== 'ouverte') {
        const type = ANSWER_TYPES.find((a) => a.value === it.answerType)?.label ?? it.answerType;
        lines.push(`   ${type}${it.options ? ` : ${it.options}` : ''}`);
      }
    }
  }
  if (p.closing) lines.push('', 'Conclusion :', p.closing);
  return lines.join('\n');
}
